import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {useGetOperationItemsQuery} from '@dms/api/finance';
import {GetCurrenciesApiResponse} from '@dms/api/shared';
import {GetCodeListResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromCurrencies} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {WarehouseDetailForm} from '../types/WarehouseDetailForm';
import {getOptionsFromCodeList} from '../utils/getOptionsFromCodeList';

interface AccountingFormProps extends TestIdProps {
  control: FormControl<WarehouseDetailForm>;
  warehousePricing: GetCodeListResponse | Nullish;
  currencies: GetCurrenciesApiResponse | Nullish;
}

export function AccountingForm(props: AccountingFormProps) {
  const {data: operationItems, isLoading: isOperationItemsLoading} = useGetOperationItemsQuery({
    application: 'workshop',
    itemGroup: 'material',
  });

  const operationItemOptions =
    operationItems?.map((item) => ({
      label: item.itemCodeName,
      value: item.operationId,
    })) ?? [];

  return (
    <VStack spacing={6}>
      <Heading size={3}>{i18n.t('entity.warehouse.labels.accounting')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="warehousePricing"
            type="chips"
            label={i18n.t('entity.warehouse.labels.valuation')}
            options={getOptionsFromCodeList(props.warehousePricing)}
            control={props.control}
            isRequired
            data-testid={suffixTestId('inputs.warehousePricing', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="currency"
            type="choice"
            label={i18n.t('general.labels.currency')}
            options={getOptionsFromCurrencies(props.currencies)}
            control={props.control}
            isRequired
            isDisabled
            data-testid={suffixTestId('inputs.currency', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            name="workItemGroupId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.defaultWorkItemGroup')}
            options={operationItemOptions}
            isLoading={isOperationItemsLoading}
            control={props.control}
            data-testid={suffixTestId('inputs.workItemGroupId', props)}
          />
        </GridItem>
      </Grid>

      <FormField
        name="isFastTrack"
        type="checkbox"
        label={i18n.t('entity.warehouse.labels.enableFastTrackIssues')}
        control={props.control}
        data-testid={suffixTestId('inputs.isFastTrack', props)}
      />

      <FormField
        name="dummyWarehouse"
        type="checkbox"
        label={i18n.t('entity.warehouse.labels.dummyWarehouse')}
        control={props.control}
        data-testid={suffixTestId('inputs.dummyWarehouse', props)}
      />
    </VStack>
  );
}
